import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Separator,
  Heading,
  Collapsible,
} from "@chakra-ui/react-v3";
import type { CareGapRowData } from "../tables/care-gaps/care-gaps-rows";
import { Tooltip } from "../../../ui/tooltip";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { ResourceAnalysis } from "../components/resource-analysis";

export type CareGapsSidePanelContentProps = {
  data: CareGapRowData;
};

/**
 * Side panel content for care gaps.
 * Shows measure details, population counts, and supporting evidence.
 */
export function CareGapsSidePanelContent({ data }: CareGapsSidePanelContentProps) {
  return (
    <SidePanelLayout title={data.formattedMeasureName}>
      {/* AI Analysis Section (if available) */}
      <ResourceAnalysis />

      {/* Basic Information */}
      <Box>
        <VStack align="stretch" gap={3}>
          <DetailRow label="Measure Name" value={data.measureName} />
          <DetailRow label="Status">
            <Badge colorPalette={data.statusColor} variant="subtle" size="sm">
              {data.formattedStatus}
            </Badge>
          </DetailRow>
          <DetailRow label="Period" value={data.period} />
          <DetailRow label="Last Run" value={data.formattedLastRun} />
          <DetailRow label="Measure URI" value={data.originalData.measureReport.measure} />
        </VStack>
      </Box>

      <Separator borderColor="border" />

      {/* Population Counts */}
      <PopulationSection data={data} />

      {/* Supporting Evidence */}
      {data.originalData.supportingEvidence &&
        Object.keys(data.originalData.supportingEvidence).length > 0 && (
          <>
            <Separator borderColor="border" />
            <SupportingEvidenceSection evidence={data.originalData.supportingEvidence} />
          </>
        )}
    </SidePanelLayout>
  );
}

/**
 * Display a single detail row with label and value
 */
function DetailRow({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <HStack justify="space-between" align="start">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted" minW="120px">
        {label}:
      </Text>
      {children ? (
        <Box textAlign="right" flex={1}>
          {children}
        </Box>
      ) : (
        <Text fontSize="sm" textAlign="right" flex={1} color="fg">
          {value}
        </Text>
      )}
    </HStack>
  );
}

/**
 * Display population status grouped by measure group
 */
function PopulationSection({ data }: { data: CareGapRowData }) {
  if (!data.formattedGroups || data.formattedGroups.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={3} color="fg">
          Population Status
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          No population data available
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={3} color="fg">
        Population Status
      </Heading>
      <VStack align="stretch" gap={4}>
        {data.formattedGroups.map(group => (
          <Box
            key={group.id}
            border="1px"
            borderColor="border"
            borderRadius="md"
            p={4}
            bg="bg.subtle"
          >
            <HStack align="center" gap={2} mb={3}>
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                {group.formattedCode}
              </Text>
              <Badge colorPalette={group.statusColor} variant="subtle" size="sm">
                {group.formattedStatus}
              </Badge>
            </HStack>
            <VStack align="stretch" gap={2}>
              {group.populations.map((pop, popIndex) => (
                <HStack key={`${group.id}-pop-${popIndex}`} justify="space-between">
                  {pop.tooltipText ? (
                    <Tooltip content={pop.tooltipText} showArrow positioning={{ placement: "top" }}>
                      <Text
                        fontSize="sm"
                        color="fg.muted"
                        cursor="help"
                        textDecoration="underline"
                        textDecorationStyle="dotted"
                      >
                        {pop.formattedCode}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text fontSize="sm" color="fg.muted">
                      {pop.formattedCode}
                    </Text>
                  )}
                  <Badge colorPalette={pop.hasCount ? "green" : "red"} variant="solid" size="sm">
                    {pop.hasCount ? "true" : "false"}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

/**
 * Display supporting evidence as expandable JSON
 */
function SupportingEvidenceSection({ evidence }: { evidence: Record<string, unknown> }) {
  return (
    <Box>
      <Heading size="md" mb={3} color="fg">
        Supporting Evidence
      </Heading>
      <Collapsible.Root>
        <Collapsible.Trigger asChild>
          <HStack
            cursor="pointer"
            p={3}
            border="1px"
            borderColor="border"
            borderRadius="md"
            _hover={{ bg: "interactive.hover" }}
          >
            <Text fontSize="sm" fontWeight="medium" color="fg">
              View JSON Data
            </Text>
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box
            mt={2}
            p={3}
            border="1px"
            borderColor="border"
            borderRadius="md"
            bg="bg"
            maxH="400px"
            overflowY="auto"
          >
            <pre style={{ fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {JSON.stringify(evidence, null, 2)}
            </pre>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
